import { useEffect, useState } from "react";
import { importAdsFeed } from "@/lib/adsAssistantClient";
import { useAdsAssistantOrgLocations } from "@/components/ads-assistant/useAdsAssistantOrgLocations";
import { useAdsAssistantFeedImportLocations } from "@/components/ads-assistant/useAdsAssistantFeedImportLocations";
import { formatAdsFeedImportResult } from "@/components/ads-assistant/adsFeedImportResultText";
import { runAdsFeedImportAction } from "@/components/ads-assistant/runAdsFeedImportAction";
import { useAdsAssistantFeedImportVaults } from "@/components/ads-assistant/useAdsAssistantFeedImportVaults";
import type {
  AdsFeedImportLocationRow,
  AdsFeedImportMode,
} from "@/components/ads-assistant/adsFeedImportSectionTypes";

export type { AdsFeedImportMode };
export type LocationRow = AdsFeedImportLocationRow;

export function useAdsAssistantFeedImport(
  scopedOrgId: string | null,
  opts?: { salesGhlOnly?: boolean; settingsLocationId?: string | null },
) {
  const salesGhlOnly = opts?.salesGhlOnly ?? false;
  const settingsLocationId = opts?.settingsLocationId?.trim() || null;
  const { locations: orgLocations, locationsLoaded } = useAdsAssistantOrgLocations(scopedOrgId);
  const { locations, salesMustPickLocation, defaultLocationId } = useAdsAssistantFeedImportLocations(
    orgLocations,
    locationsLoaded,
  );
  const vaults = useAdsAssistantFeedImportVaults(scopedOrgId, !salesGhlOnly);

  const [mode, setMode] = useState<AdsFeedImportMode>("ghl_social_planner");
  const [locationId, setLocationId] = useState("");
  const [pageId, setPageId] = useState("");
  const [overrideToken, setOverrideToken] = useState("");
  const [fetchLimit, setFetchLimit] = useState(25);
  const [daysBack, setDaysBack] = useState(30);
  const [pasteJson, setPasteJson] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    setResult(null);
  }, [mode, scopedOrgId]);

  useEffect(() => {
    if (vaults.vaultError) setError(vaults.vaultError);
  }, [vaults.vaultError]);

  useEffect(() => {
    if (!locationsLoaded || !defaultLocationId || locationId) return;
    setLocationId(defaultLocationId);
  }, [locationsLoaded, defaultLocationId, locationId]);

  useEffect(() => {
    if (mode !== "facebook_graph_fetch") return;
    if (pageId.trim()) return;
    if (!locationId) return;
    const selected = locations.find((l) => l.id === locationId);
    const mapped =
      typeof selected?.social_facebook_page_id === "string" ? selected.social_facebook_page_id.trim() : "";
    if (mapped) setPageId(mapped);
  }, [locationId, locations, mode, pageId]);

  const resolveLocationId = (): string | null => {
    if (salesGhlOnly && settingsLocationId) return settingsLocationId;
    return locationId.trim() || null;
  };

  const runReconcileLocationTags = async () => {
    if (!scopedOrgId) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const r = await importAdsFeed({ mode: "ghl_social_planner", reconcile_only: true });
      if (!r.ok) {
        setError(r.error);
        return;
      }
      setResult(formatAdsFeedImportResult(r.data));
    } finally {
      setBusy(false);
    }
  };

  const runImport = async () => {
    if (!scopedOrgId) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const out = await runAdsFeedImportAction({
        mode,
        location_id: resolveLocationId(),
        pageId,
        overrideToken,
        canEditMetaToken: vaults.canEditMetaToken,
        pasteJson,
        fetchLimit,
        daysBack,
        encryptionConfigured: vaults.encryptionConfigured,
        metaConfigured: vaults.metaConfigured,
        googleEncryptionConfigured: vaults.googleEncryptionConfigured,
        googleConfigured: vaults.googleConfigured,
        salesGhlOnly,
        settingsLocationId,
        salesMustPickLocation,
      });
      if (!out.ok) {
        setError(out.error);
        return;
      }
      setResult(out.resultText);
      if (out.clearOverride) setOverrideToken("");
    } finally {
      setBusy(false);
    }
  };

  const fetchReady = salesGhlOnly
    ? Boolean(settingsLocationId)
    : mode === "ghl_social_planner" ||
      mode === "google_local_posts_paste" ||
      mode === "facebook_graph_paste" ||
      (mode === "facebook_graph_fetch" &&
        vaults.encryptionConfigured &&
        (vaults.metaConfigured || (!!vaults.canEditMetaToken && !!overrideToken.trim()))) ||
      (mode === "google_gbp_fetch" && vaults.googleEncryptionConfigured && vaults.googleConfigured);

  const importReady = salesGhlOnly
    ? fetchReady
    : fetchReady && (!salesMustPickLocation || !!locationId.trim());

  return {
    mode,
    setMode,
    locationId,
    setLocationId,
    locations,
    locationsLoaded,
    pageId,
    setPageId,
    overrideToken,
    setOverrideToken,
    saveTokenDraft: vaults.saveTokenDraft,
    setSaveTokenDraft: vaults.setSaveTokenDraft,
    fetchLimit,
    setFetchLimit,
    daysBack,
    setDaysBack,
    salesMustPickLocation,
    pasteJson,
    setPasteJson,
    busy,
    error,
    result,
    metaLoading: vaults.metaLoading,
    metaConfigured: vaults.metaConfigured,
    metaUpdatedAt: vaults.metaUpdatedAt,
    encryptionConfigured: vaults.encryptionConfigured,
    canEditMetaToken: vaults.canEditMetaToken,
    metaBanner: vaults.metaBanner,
    saveVaultBusy: vaults.saveVaultBusy,
    fetchReady,
    importReady,
    saveOrgToken: vaults.saveOrgToken,
    clearOrgToken: vaults.clearOrgToken,
    googleLoading: vaults.googleLoading,
    googleConfigured: vaults.googleConfigured,
    googleUpdatedAt: vaults.googleUpdatedAt,
    googleEncryptionConfigured: vaults.googleEncryptionConfigured,
    canEditGoogleCredentials: vaults.canEditGoogleCredentials,
    googleBanner: vaults.googleBanner,
    saveCredentialsDraft: vaults.saveCredentialsDraft,
    setSaveCredentialsDraft: vaults.setSaveCredentialsDraft,
    saveOrgGoogleCredentials: vaults.saveOrgGoogleCredentials,
    clearOrgGoogleCredentials: vaults.clearOrgGoogleCredentials,
    runImport,
    runReconcileLocationTags,
  };
}
